const Field = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className={`mt-0.5 text-sm ${value ? "text-charcoal" : "text-terracotta"}`}>{value || "Not found — review"}</p>
  </div>
);

const TAG = { IMPORTED: "badge-available", EXCLUDED: "badge-neutral", UNRESOLVED: "badge-sold", INVALID: "badge-sold" };

const LineStatus = ({ s }) => (s ? (
  <span className={`ml-1 badge-status ${TAG[s.status]}`} data-testid={`pdf-line-status-${s.row}`}>
    {s.status === "IMPORTED" ? `→ ${s.sku}` : s.status === "EXCLUDED" ? "Excluded" : s.status === "UNRESOLVED" ? "Unresolved" : "Not imported"}
  </span>
) : null);

const PalletRow = ({ p, status }) => (
  <li className="rounded-md border border-border px-3 py-2" data-testid={`pdf-pallet-${p.pallet ?? "unassigned"}`}>
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="font-mono font-medium text-charcoal">Pallet {p.pallet ?? "?"}</span>
      <span className="text-warmgrey">{p.boxes} boxes</span>
      {p.mixed && <span className="badge-status badge-neutral">Mixed · {p.lines.length} SKUs</span>}
    </div>
    <ul className="mt-1 space-y-0.5 text-xs text-charcoal">
      {p.lines.map((l) => (
        <li key={l.row} data-testid={`pdf-line-${l.row}`}><span className="font-mono">{l.sku}</span> · {l.description} · <strong>{l.boxes ?? "?"}</strong> boxes<LineStatus s={status?.[l.row]} /></li>
      ))}
    </ul>
  </li>
);

export const PdfExtractionPanel = ({ pdf, finalPallets }) => {
  const h = pdf.header || {};
  const status = finalPallets ? Object.fromEntries(finalPallets.flatMap((p) => p.lines).map((l) => [l.row, l])) : null;
  return (
    <div className="space-y-4 rounded-md border border-border bg-alabaster p-4" data-testid="pdf-extraction-panel">
      <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Read from PDF — check before confirming</p>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Field label="Container" value={h.container && `${h.container}${h.container_type ? ` (${h.container_type})` : ""}`} testid="pdf-container" />
        <Field label="Supplier reference" value={h.supplier_reference} testid="pdf-supplier-ref" />
        <Field label="PO" value={h.po_number} testid="pdf-po" />
        <Field label="Supplier" value={h.supplier_legal_name} testid="pdf-supplier" />
        <Field label="Document date" value={h.date} testid="pdf-date" />
        <Field label="Pallets" value={`${pdf.pallets.length} (PDF says ${pdf.declared_pallets ?? "?"})`} testid="pdf-pallets" />
        <Field label="Boxes" value={`${pdf.extracted_boxes} (PDF says ${pdf.declared_boxes ?? "?"})`} testid="pdf-boxes" />
      </div>
      <ul className="space-y-1 text-xs" data-testid="pdf-checks">
        {pdf.checks.map((c) => (
          <li key={c.check} className={c.ok ? "text-charcoal" : "text-terracotta"}>{c.ok ? "✓" : "✗"} {c.check} — {c.detail}</li>
        ))}
      </ul>
      {pdf.review.length > 0 && (
        <div className="rounded-md border border-terracotta/40 bg-terracotta/5 p-3 text-xs text-charcoal" data-testid="pdf-review">
          <p className="font-semibold">Needs manual review ({pdf.review.length})</p>
          <ul className="mt-1 space-y-0.5">{pdf.review.map((r, i) => <li key={i}>{r.field}: {r.issue}</li>)}</ul>
        </div>
      )}
      <ul className="grid max-h-72 grid-cols-1 gap-2 overflow-y-auto md:grid-cols-2" data-testid="pdf-pallet-list">
        {pdf.pallets.map((p) => <PalletRow key={p.pallet ?? "x"} p={p} status={status} />)}
      </ul>
    </div>
  );
};
